import { ClipboardList } from 'lucide-react';
import {
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@insolech/ui';
import type { orderQueries } from '@insolech/sdk';

/**
 * StrandedDraftsTable - admin-only table fed by the
 * public.admin_stranded_drafts view (draft orders older than 24 hours with
 * zero order_status_history rows). Rendered by OrderTrackerPage when the
 * "Stalled drafts" chip is active (?status=stranded).
 *
 * Deliberately lightweight and informational: rows come pre-sorted
 * oldest-first from listStrandedDrafts, there is no pagination (the set is
 * small by definition), and clicking a row opens the admin order detail
 * page for review. Submitting or deleting a stalled draft happens through
 * the existing tracker actions under the Pending/Active tabs, not here.
 * Styling mirrors the main tracker table (sticky sand-50 header, same cell
 * paddings and empty/error states).
 */

const COLUMN_COUNT = 6;

function formatAge(createdAt: string): string {
  const ms = Date.now() - new Date(createdAt).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return '-';
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 48) return hours <= 1 ? '1 hour' : `${hours} hours`;
  const days = Math.floor(hours / 24);
  return `${days} days`;
}

function formatDateTime(s: string): string {
  try {
    return new Date(s).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s;
  }
}

export function StrandedDraftsTable({
  rows,
  loading,
  error,
  onRowClick,
}: {
  rows: orderQueries.StrandedDraftRow[];
  loading: boolean;
  /** Error object from the list query; null/undefined when healthy. */
  error: unknown;
  /** Navigate to the admin order detail page for this order id. */
  onRowClick: (orderId: string) => void;
}) {
  return (
    <div data-testid="stranded-drafts-table">
      {/* Context line so an admin landing here knows what qualifies a row.
          Muted: this surface is informational, drafts can be parked on
          purpose. */}
      <p className="px-4 pb-2 text-xs text-text-muted">
        Draft orders created more than 24 hours ago that were never submitted.
        Some may be parked on purpose; click a row to open the order for
        review. To submit or delete a draft, use the order actions under the
        Pending or Active tabs.
      </p>
      <Table wrapperClassName="max-h-[calc(100vh-260px)] overflow-auto">
        <TableHeader className="sticky top-0 z-20 bg-sand-50 shadow-[0_1px_0_0_var(--color-border-subtle)]">
          <TableRow>
            <TableHead>Order ID</TableHead>
            <TableHead>Clinic</TableHead>
            <TableHead>Podiatrist</TableHead>
            <TableHead>Age</TableHead>
            <TableHead className="text-center">Lines</TableHead>
            <TableHead className="text-end">Total (AED)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`stranded-sk-${i}`}>
                <TableCell colSpan={COLUMN_COUNT}>
                  <Skeleton className="h-6 w-full" />
                </TableCell>
              </TableRow>
            ))
          ) : error ? (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT}>
                <div className="text-error-700 text-sm py-4 space-y-1">
                  <div>Could not load stalled drafts.</div>
                  <div className="font-mono text-xs whitespace-pre-wrap break-all">
                    {error instanceof Error
                      ? error.message
                      : String((error as { message?: string })?.message ?? error)}
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT}>
                <EmptyState
                  icon={<ClipboardList className="h-6 w-6" />}
                  title="No stalled drafts"
                  description="Every draft older than 24 hours has been submitted or is new."
                />
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer"
                onClick={() => onRowClick(row.id)}
                data-testid={`stranded-row-${row.order_number}`}
              >
                <TableCell className="py-2 font-mono text-xs text-text-primary whitespace-nowrap hover:underline">
                  {row.order_number}
                </TableCell>
                <TableCell className="py-2 whitespace-nowrap truncate max-w-[220px]">
                  {row.clinic_name || <span className="text-text-muted">-</span>}
                </TableCell>
                <TableCell className="py-2 whitespace-nowrap truncate max-w-[200px]">
                  {row.podiatrist_name ?? <span className="text-text-muted">-</span>}
                </TableCell>
                <TableCell
                  className="py-2 whitespace-nowrap text-text-secondary"
                  title={`Created ${formatDateTime(row.created_at)}`}
                >
                  {formatAge(row.created_at)}
                </TableCell>
                <TableCell className="py-2 tabular whitespace-nowrap text-center">
                  {row.line_count}
                </TableCell>
                <TableCell className="py-2 tabular whitespace-nowrap text-end">
                  {row.total_aed.toFixed(2)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
